import { useCallback, useEffect, useMemo, useState } from 'react';
import { createRelease, fetchReleasingWorkspace, reopenRelease } from '../services/releasingService';
import { refreshNavigationNotifications } from './useNavigationNotifications';

const RELEASING_TAB_STORAGE_KEY = 'seaweeds.releasing.activeTab';
const RELEASING_TAB_IDS = new Set(['pending', 'history', 'inventory']);
const KG_ROUNDING_TOLERANCE = 0.005;

function useReleasings() {
  const [activeTab, setActiveTabState] = useState(loadStoredReleasingTab);
  const [pendingSalesOrders, setPendingSalesOrders] = useState([]);
  const [releaseHistory, setReleaseHistory] = useState([]);
  const [availableInventory, setAvailableInventory] = useState([]);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);
  const [selectedFclSlot, setSelectedFclSlot] = useState(null);
  const [includePreReleaseInventory, setIncludePreReleaseInventory] = useState(false);
  const [releaseDate, setReleaseDate] = useState(getTodayInputValue);
  const [remarks, setRemarks] = useState('');
  const [expensePerSack, setExpensePerSack] = useState('');
  const [allocations, setAllocations] = useState({});
  const [allocationErrors, setAllocationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadWorkspace = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }

    setLoadError('');

    try {
      const workspace = await fetchReleasingWorkspace();
      setPendingSalesOrders(workspace.pendingSalesOrders || []);
      setReleaseHistory(workspace.releaseHistory || []);
      setAvailableInventory(workspace.availableInventory || []);
    } catch (error) {
      setLoadError(error.message);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const setActiveTab = useCallback((nextTab) => {
    const normalizedTab = RELEASING_TAB_IDS.has(nextTab) ? nextTab : 'pending';
    setActiveTabState(normalizedTab);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(RELEASING_TAB_STORAGE_KEY, normalizedTab);
    }
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);

  const draftInventory = useMemo(
    () => mapSelectedDraftInventory(selectedFclSlot),
    [selectedFclSlot]
  );
  const inventoryWithSelectedDraft = useMemo(
    () => mergeInventoryWithSelectedDraft(availableInventory, draftInventory),
    [availableInventory, draftInventory]
  );
  const filteredInventory = useMemo(
    () =>
      includePreReleaseInventory
        ? inventoryWithSelectedDraft
        : inventoryWithSelectedDraft.filter((item) => !item.isPreReleaseCandidate),
    [includePreReleaseInventory, inventoryWithSelectedDraft]
  );
  const preReleaseCandidateCount = useMemo(
    () => inventoryWithSelectedDraft.filter((item) => item.isPreReleaseCandidate).length,
    [inventoryWithSelectedDraft]
  );

  const allocationTotals = useMemo(
    () => calculateAllocationTotals(selectedSalesOrder, filteredInventory, allocations),
    [allocations, filteredInventory, selectedSalesOrder]
  );
  const allocatedKg = allocationTotals.kg;
  const hasSelectedPreReleaseAllocations = useMemo(
    () => hasPreReleaseAllocations(filteredInventory, allocations),
    [allocations, filteredInventory]
  );
  const allocatedOrderQuantity = selectedSalesOrder && allocatedKg > 0 ? 1 : 0;
  const remainingAfterAllocation = selectedSalesOrder
    ? Math.max(Number(selectedSalesOrder.remainingQuantity || 0) - allocatedOrderQuantity, 0)
    : 0;

  function openReleaseModal(salesOrder, fclSlot = null) {
    const isSavedDraft = String(fclSlot?.release?.status || '') === 'Pre-Released';
    const shouldShowPreReleaseInventory =
      isSavedDraft && hasPreReleaseDraftQuantity(fclSlot.release.items);
    setSelectedSalesOrder(salesOrder);
    setSelectedFclSlot(fclSlot);
    setIncludePreReleaseInventory(shouldShowPreReleaseInventory);
    setReleaseDate(
      toDateInputValue(fclSlot?.release?.releaseDate) ||
        toDateInputValue(salesOrder.deliveryDate) ||
        getTodayInputValue()
    );
    setRemarks(fclSlot?.release?.remarks || '');
    setExpensePerSack(
      fclSlot?.release && fclSlot.release.expensePerSack !== undefined
        ? normalizeQuantityInput(String(fclSlot.release.expensePerSack || 0))
        : ''
    );
    setAllocations(isSavedDraft ? buildDraftAllocations(fclSlot.release.items) : {});
    setAllocationErrors({});
    setSubmitError('');
    setSuccessMessage('');
  }

  function closeReleaseModal() {
    if (isSaving) {
      return;
    }

    setSelectedSalesOrder(null);
    setSelectedFclSlot(null);
    setAllocations({});
    setAllocationErrors({});
    setSubmitError('');
  }

  function handleAllocationChange(inventoryItem, value) {
    const normalizedValue = normalizeWholeSacksInput(value);
    const nextValue =
      inventoryItem.isPreReleaseCandidate && Number(normalizedValue || 0) > 0
        ? String(getAvailableReleaseQuantity(inventoryItem))
        : normalizedValue;

    setAllocations((currentAllocations) => ({
      ...currentAllocations,
      [inventoryItem.sourceKey]: nextValue,
    }));
    setAllocationErrors((currentErrors) => ({
      ...currentErrors,
      [inventoryItem.sourceKey]: '',
    }));
    setSubmitError('');
  }

  function handleExpensePerSackChange(value) {
    setExpensePerSack(normalizeQuantityInput(value));
    setAllocationErrors((currentErrors) => ({
      ...currentErrors,
      expensePerSack: '',
    }));
    setSubmitError('');
  }

  function handleAutoSelectProfitableAllocations() {
    if (!selectedSalesOrder) {
      return;
    }

    const requiredNetKg = getFclSlotNetKg(selectedFclSlot);
    const nextAllocations = buildProfitableReleaseAllocations({
      inventoryItems: filteredInventory,
      requiredNetKg,
    });

    setAllocations(nextAllocations);
    setAllocationErrors((currentErrors) => ({
      ...currentErrors,
      form: '',
    }));
    setSubmitError('');
  }

  function handleResetAllocations() {
    setAllocations({});
    setAllocationErrors((currentErrors) => ({
      ...currentErrors,
      form: '',
    }));
    setSubmitError('');
  }

  function validateReleaseDraft(options = {}) {
    if (!selectedSalesOrder) {
      return false;
    }

    const nextErrors = validateAllocations({
      selectedSalesOrder,
      selectedFclSlot,
      availableInventory: filteredInventory,
      allocations,
      saveMode: options.saveMode,
      isEditingDraft: String(selectedFclSlot?.release?.status || '') === 'Pre-Released',
    });
    const normalizedExpensePerSack = Number(expensePerSack || 0);

    const isDraftSave = options.saveMode === 'draft';

    if (
      !isDraftSave &&
      (!expensePerSack || Number.isNaN(normalizedExpensePerSack) || normalizedExpensePerSack <= 0)
    ) {
      nextErrors.expensePerSack = 'Expense Per Sack is required.';
    }

    setAllocationErrors(nextErrors);

    return !Object.keys(nextErrors).length;
  }

  async function handleSubmitRelease(event, options = {}) {
    event.preventDefault();

    if (!selectedSalesOrder) {
      return false;
    }

    if (!validateReleaseDraft(options)) {
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      const isDraftSave = options.saveMode === 'draft';
      const isEditingDraft = selectedFclSlot?.release?.status === 'Pre-Released';
      const draftInventorySnapshot = isDraftSave
        ? buildDraftInventorySnapshot(filteredInventory, selectedFclSlot?.release?.items)
        : new Map();

      const savedRelease = await createRelease({
        releaseId:
          isEditingDraft
            ? selectedFclSlot.release.id
            : null,
        salesOrderId: selectedSalesOrder.id,
        slotCode: selectedFclSlot?.slotCode || '',
        slotSacks: parsePositiveNumber(selectedFclSlot?.draft?.sacks),
        slotGrossKg: parsePositiveNumber(selectedFclSlot?.draft?.grossKg),
        saveMode: isDraftSave ? 'draft' : 'release',
        releaseDate,
        expensePerSack: Number(expensePerSack || 0),
        remarks,
        items: filteredInventory
          .map((item) => ({
            sourceKey: item.sourceKey,
            purchaseOrderId: item.purchaseOrderId,
            mergeId: item.mergeId,
            poCode: item.poCode,
            poStatus: item.poStatus,
            quantity: item.quantity,
            quantityUnit: item.quantityUnit,
            quantityLabel: item.quantityLabel,
            originalQuantity: item.originalQuantity || item.quantity,
            releaseQuantity: Number(allocations[item.sourceKey] || 0),
            quantityKg:
              isDraftSave && item.isPreReleaseCandidate
                ? 0
                : convertReleaseQuantityToKg(
                    item,
                    selectedSalesOrder,
                    allocations[item.sourceKey]
                  ),
            isPreReleaseCandidate: Boolean(item.isPreReleaseCandidate),
          }))
          .filter((item) =>
            isDraftSave && item.isPreReleaseCandidate
              ? Number(item.releaseQuantity || 0) > 0
              : item.quantityKg > 0
          ),
      });
      const shouldClearDraft = isEditingDraft && !hasPositiveDraftQuantity(allocations);

      if (isDraftSave && !shouldClearDraft && savedRelease) {
        const enrichedSavedRelease = enrichDraftReleaseItems(savedRelease, draftInventorySnapshot);

        setSelectedFclSlot((currentSlot) =>
          currentSlot
            ? {
                ...currentSlot,
                release: enrichedSavedRelease,
                status: 'Saved as Draft',
              }
            : currentSlot
        );
        setIncludePreReleaseInventory(hasPreReleaseDraftQuantity(enrichedSavedRelease.items));
      }

      if (!isDraftSave || shouldClearDraft) {
        closeReleaseModal();
        if (!isDraftSave) {
          setActiveTab('history');
        }
      }
      setSuccessMessage(
        shouldClearDraft
          ? 'Pre-release draft cleared successfully.'
          : isDraftSave
          ? 'Pre-release draft saved successfully.'
          : 'Inventory released successfully.'
      );
      await loadWorkspace(false);
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReopenRelease(releaseId) {
    const normalizedReleaseId = Number(releaseId || 0);

    if (!normalizedReleaseId || isSaving) {
      return false;
    }

    setIsSaving(true);
    setSubmitError('');

    try {
      await reopenRelease(normalizedReleaseId);
      setSuccessMessage('Release reopened as draft.');
      await loadWorkspace(false);
      refreshNavigationNotifications();
      return true;
    } catch (error) {
      setSubmitError(error.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }

  return {
    activeTab,
    pendingSalesOrders,
    releaseHistory,
    availableInventory,
    filteredInventory,
    selectedSalesOrder,
    selectedFclSlot,
    includePreReleaseInventory,
    preReleaseCandidateCount,
    releaseDate,
    remarks,
    expensePerSack,
    allocations,
    allocationErrors,
    allocationTotals,
    allocatedKg,
    hasSelectedPreReleaseAllocations,
    allocatedOrderQuantity,
    remainingAfterAllocation,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    setActiveTab,
    setReleaseDate,
    setRemarks,
    setExpensePerSack: handleExpensePerSackChange,
    setIncludePreReleaseInventory,
    openReleaseModal,
    closeReleaseModal,
    handleAllocationChange,
    handleAutoSelectProfitableAllocations,
    handleResetAllocations,
    validateReleaseDraft,
    handleSubmitRelease,
    handleReopenRelease,
  };
}

function buildProfitableReleaseAllocations({
  inventoryItems,
  requiredNetKg,
}) {
  const nextAllocations = {};
  let selectedKg = 0;
  const targetKg = Number(requiredNetKg || 0);

  sortInventoryByProfitPriority(inventoryItems).some((item) => {
    if (item.isPreReleaseCandidate || item.isReservedByDraft) {
      return false;
    }

    const availableSacks = getAvailableReleaseQuantity(item);
    const kgPerSack = getKgPerReleaseUnit(item);

    if (availableSacks <= 0 || kgPerSack <= 0) {
      return false;
    }

    const remainingKg = targetKg > 0 ? Math.max(targetKg - selectedKg, 0) : Number(item.availableKg || 0);
    const rawSacksToRelease = targetKg > 0
      ? Math.min(availableSacks, Math.max(1, Math.ceil(remainingKg / kgPerSack)))
      : availableSacks;
    const sacksToRelease = Math.min(availableSacks, rawSacksToRelease);

    if (sacksToRelease > 0) {
      nextAllocations[item.sourceKey] = String(sacksToRelease);
      selectedKg = roundQuantity(selectedKg + sacksToRelease * kgPerSack);
    }

    return targetKg > 0 && selectedKg >= targetKg;
  });

  return nextAllocations;
}

function hasPreReleaseAllocations(inventoryItems, allocations) {
  return inventoryItems.some(
    (item) =>
      item.isPreReleaseCandidate &&
      Number(allocations[item.sourceKey] || 0) > 0
  );
}

function hasPreReleaseDraftQuantity(items = []) {
  return items.some((item) => {
    const poStatus = String(item.poStatus || '').trim();
    const isPreReleaseCandidate = !['', 'Completed'].includes(poStatus);

    return isPreReleaseCandidate && Number(item.releaseQuantity || 0) > 0;
  });
}

function buildDraftInventorySnapshot(inventoryItems, existingDraftItems = []) {
  const snapshot = new Map();

  existingDraftItems.forEach((item) => {
    if (item.sourceKey) {
      snapshot.set(item.sourceKey, item);
    }
  });

  inventoryItems.forEach((item) => {
    if (item.sourceKey) {
      snapshot.set(item.sourceKey, item);
    }
  });

  return snapshot;
}

function enrichDraftReleaseItems(savedRelease, inventoryBySourceKey) {
  return {
    ...savedRelease,
    items: (savedRelease.items || []).map((item) => {
      const inventoryItem = inventoryBySourceKey.get(item.sourceKey);

      if (!inventoryItem) {
        return item;
      }

      return {
        ...item,
        poStatus: item.poStatus || inventoryItem.poStatus || '',
        quantity: getStableInventoryQuantity(inventoryItem, item),
        quantityUnit: inventoryItem.quantityUnit || item.quantityUnit || 'sacks',
        quantityLabel:
          inventoryItem.quantityLabel ||
          item.quantityLabel ||
          formatQuantityLabel(getStableInventoryQuantity(inventoryItem, item), inventoryItem.quantityUnit || item.quantityUnit || 'sacks'),
        originalQuantity: getStableInventoryQuantity(inventoryItem, item),
        draftReleaseQuantity: Number(item.releaseQuantity || 0),
        sacksMark: item.sacksMark || inventoryItem.sacksMark || '',
        supplierName: item.supplierName || inventoryItem.supplierName || '',
        warehouseLocation: item.warehouseLocation || inventoryItem.warehouseLocation || '',
        lansaName: item.lansaName || inventoryItem.lansaName || '',
        seaweedType: item.seaweedType || inventoryItem.seaweedType || '',
      };
    }),
  };
}

function getStableInventoryQuantity(inventoryItem, savedItem) {
  return Number(
    inventoryItem.originalQuantity ||
      inventoryItem.quantity ||
      savedItem.originalQuantity ||
      savedItem.quantity ||
      0
  );
}

function formatQuantityLabel(quantity, unit) {
  return quantity > 0 ? `${quantity.toLocaleString('en-US')} ${unit || 'sacks'}` : '';
}

function mapSelectedDraftInventory(selectedFclSlot) {
  const release = selectedFclSlot?.release;

  if (String(release?.status || '') !== 'Pre-Released' || !Array.isArray(release.items)) {
    return [];
  }

  return release.items.map((item) => {
    const poStatus = String(item.poStatus || '').trim();
    const isPreReleaseCandidate = !['', 'Completed'].includes(poStatus);
    const releaseQuantity = Number(item.releaseQuantity || 0);
    const originalQuantity = isPreReleaseCandidate
      ? Number(item.originalQuantity || item.quantity || releaseQuantity || 0)
      : Number(releaseQuantity || item.originalQuantity || item.quantity || 0);
    const quantityUnit = item.quantityUnit || 'sacks';

    return {
      id: item.sourceKey || `draft-${item.id}`,
      sourceKey: item.sourceKey,
      sourceType: item.sourceType || 'pre-release',
      purchaseOrderId: item.purchaseOrderId || null,
      mergeId: item.mergeId || null,
      receivingId: item.receivingId || null,
      poCode: item.poCode || '',
      sacksMark: item.sacksMark || '',
      supplierName: item.supplierName || '',
      warehouseLocation: item.warehouseLocation || '',
      lansaName: item.lansaName || '',
      seaweedType: item.seaweedType || '',
      poStatus: poStatus || 'Draft',
      isPreReleaseCandidate,
      quantity: originalQuantity,
      quantityUnit,
      quantityLabel:
        originalQuantity > 0
          ? `${originalQuantity.toLocaleString('en-US')} ${quantityUnit}`
          : '',
      originalQuantity,
      draftReleaseQuantity: releaseQuantity,
      originalQuantityKg: Number(item.quantityKg || 0),
      originalKg: Number(item.quantityKg || 0),
      releasedKg: 0,
      availableKg: Number(item.quantityKg || 0),
      unitCost: Number(item.unitCost || 0),
      receivedAt: release.releaseDate,
      isSelectedDraftInventory: true,
    };
  });
}

function mergeInventoryWithSelectedDraft(availableInventory, draftInventory) {
  if (!draftInventory.length) {
    return availableInventory;
  }

  const draftSourceKeys = new Set(
    draftInventory.map((item) => item.sourceKey).filter(Boolean)
  );
  const availableInventoryBySourceKey = new Map(
    availableInventory
      .filter((item) => item.sourceKey)
      .map((item) => [item.sourceKey, item])
  );
  const draftPreReleasePurchaseOrderIds = new Set(
    draftInventory
      .filter((item) => item.isPreReleaseCandidate && item.purchaseOrderId)
      .map((item) => String(item.purchaseOrderId))
  );
  const visibleAvailableInventory = availableInventory.filter((item) => {
    if (draftSourceKeys.has(item.sourceKey)) {
      return false;
    }

    if (
      item.isPreReleaseCandidate &&
      item.purchaseOrderId &&
      draftPreReleasePurchaseOrderIds.has(String(item.purchaseOrderId))
    ) {
      return false;
    }

    return true;
  });

  return [
    ...visibleAvailableInventory,
    ...draftInventory
      .filter((item) => item.sourceKey)
      .map((item) => {
        const availableItem = availableInventoryBySourceKey.get(item.sourceKey);

        if (availableItem) {
          return {
            ...availableItem,
            draftReleaseQuantity: Number(item.draftReleaseQuantity || 0),
            isReservedByDraft: false,
            isSelectedDraftInventory: true,
          };
        }

        return item.isPreReleaseCandidate
          ? item
          : {
              ...item,
              availableKg: 0,
              releasedKg: Number(item.originalQuantityKg || item.originalKg || item.quantityKg || 0),
              isUnavailableDraftInventory: true,
            };
      }),
  ];
}

function buildDraftAllocations(items = []) {
  return items.reduce((nextAllocations, item) => {
    if (item.sourceKey) {
      const poStatus = String(item.poStatus || '').trim();
      const isPreReleaseCandidate = !['', 'Completed'].includes(poStatus);
      const releaseQuantity = Number(item.releaseQuantity || 0);
      const fullQuantity = Number(item.originalQuantity || item.quantity || 0);

      nextAllocations[item.sourceKey] = String(
        Math.floor(isPreReleaseCandidate && releaseQuantity > 0 ? fullQuantity : releaseQuantity)
      );
    }

    return nextAllocations;
  }, {});
}

function hasPositiveDraftQuantity(allocations = {}) {
  return Object.values(allocations).some((value) => Number(value || 0) > 0);
}

function sortInventoryByProfitPriority(inventoryItems) {
  return [...inventoryItems].sort((firstItem, secondItem) => {
    const firstUnitCost = Number(firstItem.unitCost || 0);
    const secondUnitCost = Number(secondItem.unitCost || 0);

    return (
      firstUnitCost - secondUnitCost ||
      new Date(firstItem.receivedAt || 0).getTime() -
        new Date(secondItem.receivedAt || 0).getTime() ||
      String(firstItem.poCode || '').localeCompare(String(secondItem.poCode || ''))
    );
  });
}

function validateAllocations({
  selectedSalesOrder,
  selectedFclSlot,
  availableInventory,
  allocations,
  saveMode,
  isEditingDraft,
}) {
  const errors = {};
  let totalKg = 0;
  const requiredNetKg = getFclSlotNetKg(selectedFclSlot);
  const isDraftSave = saveMode === 'draft';

  availableInventory.forEach((inventoryItem) => {
    const value = allocations[inventoryItem.sourceKey];

    if (!value) {
      return;
    }

    const releaseQuantity = Number(value);
    const quantityKg = convertReleaseQuantityToKg(inventoryItem, selectedSalesOrder, value);
    const availableReleaseQuantity = getAvailableReleaseQuantity(inventoryItem);
    const isDraftPreReleaseItem = isDraftSave && inventoryItem.isPreReleaseCandidate;

    if (releaseQuantity === 0) {
      return;
    }

    totalKg += quantityKg;

    if (!Number.isFinite(releaseQuantity) || releaseQuantity <= 0) {
      errors[inventoryItem.sourceKey] = 'Enter a quantity greater than zero.';
    }

    if (inventoryItem.isReservedByDraft) {
      errors[inventoryItem.sourceKey] = 'Reserved by another release draft.';
      return;
    }

    if (releaseQuantity > availableReleaseQuantity) {
      errors[inventoryItem.sourceKey] = inventoryItem.isUnavailableDraftInventory
        ? 'This draft source is no longer available.'
        : `Only ${availableReleaseQuantity.toLocaleString('en-US')} sacks remain available.`;
      return;
    }

    if (isDraftPreReleaseItem) {
      if (releaseQuantity !== availableReleaseQuantity) {
        errors[inventoryItem.sourceKey] = 'Pre-release P.O.s must be released in full.';
      }

      return;
    }

    if (quantityKg > Number(inventoryItem.availableKg || 0) + KG_ROUNDING_TOLERANCE) {
      errors[inventoryItem.sourceKey] = 'Cannot exceed available KG net.';
    }
  });

  if (totalKg <= 0) {
    if (isDraftSave && isEditingDraft && !hasPositiveDraftQuantity(allocations)) {
      return errors;
    }

    if (
      isDraftSave &&
      availableInventory.some(
        (inventoryItem) =>
          inventoryItem.isPreReleaseCandidate &&
          Number(allocations[inventoryItem.sourceKey] || 0) > 0
      )
    ) {
      return errors;
    }

    errors.form = 'Enter at least one quantity to release.';
  }

  if (!isDraftSave && requiredNetKg > 0 && totalKg > 0 && totalKg < requiredNetKg) {
    errors.form = `Total net kilo must be at least ${formatKg(requiredNetKg)} before saving this release.`;
  }

  if (totalKg > 0 && Number(selectedSalesOrder.remainingQuantity || 0) < 1) {
    errors.form = 'This sales order has no remaining FCL slots to release.';
  }

  return errors;
}

function calculateAllocationTotals(salesOrder, inventoryItems, allocations) {
  return inventoryItems.reduce(
    (totals, item) => {
      const value = Number(allocations[item.sourceKey] || 0);

      if (!Number.isFinite(value) || value <= 0) {
        return totals;
      }

      if (item.isReservedByDraft) {
        return totals;
      }

      if (item.isPreReleaseCandidate) {
        return {
          ...totals,
          orderQuantity: totals.orderQuantity + value,
        };
      }

      if (value > getAvailableReleaseQuantity(item)) {
        return totals;
      }

      const quantityKg = convertReleaseQuantityToKg(item, salesOrder, value);

      return {
        kg: totals.kg + quantityKg,
        orderQuantity: totals.orderQuantity + value,
        capital: totals.capital + quantityKg * Number(item.unitCost || 0),
      };
    },
    { kg: 0, orderQuantity: 0, capital: 0 }
  );
}

function convertReleaseQuantityToKg(inventoryItem, salesOrder, value) {
  const releaseQuantity = Number(value || 0);

  if (!Number.isFinite(releaseQuantity) || releaseQuantity <= 0) {
    return 0;
  }

  return roundQuantity(releaseQuantity * getKgPerReleaseUnit(inventoryItem));
}

function getAvailableReleaseQuantity(inventoryItem) {
  if (inventoryItem.isReservedByDraft) {
    return 0;
  }

  if (inventoryItem.isPreReleaseCandidate) {
    return Math.floor(getInventoryQuantity(inventoryItem));
  }

  const availableQuantity = getAvailableQuantityFromOriginalRatio(inventoryItem);

  if (availableQuantity > 0) {
    return availableQuantity;
  }

  const availableKg = Number(inventoryItem.availableKg || 0);
  const kgPerReleaseUnit = getKgPerReleaseUnit(inventoryItem);

  if (kgPerReleaseUnit <= 0) {
    return 0;
  }

  return Math.floor(availableKg / kgPerReleaseUnit);
}

function getKgPerReleaseUnit(inventoryItem) {
  if (!inventoryItem.isPreReleaseCandidate) {
    const availableKg = Number(inventoryItem.availableKg || 0);
    const availableQuantity = getAvailableQuantityFromOriginalRatio(inventoryItem);

    if (availableKg > 0 && availableQuantity > 0) {
      return availableKg / availableQuantity;
    }
  }

  const originalKg = Number(
    inventoryItem.originalKg || inventoryItem.originalQuantityKg || inventoryItem.availableKg || 0
  );
  const originalQuantity = getInventoryQuantity(inventoryItem);

  if (originalKg > 0 && originalQuantity > 0) {
    return originalKg / originalQuantity;
  }

  return 1;
}

function getAvailableQuantityFromOriginalRatio(inventoryItem) {
  const availableKg = Number(inventoryItem.availableKg || 0);
  const originalKg = Number(
    inventoryItem.originalKg || inventoryItem.originalQuantityKg || availableKg || 0
  );
  const originalQuantity = getInventoryQuantity(inventoryItem);

  if (availableKg <= 0 || originalKg <= 0 || originalQuantity <= 0) {
    return 0;
  }

  return clampWholeQuantity((availableKg / originalKg) * originalQuantity, originalQuantity);
}

function clampWholeQuantity(value, maxValue) {
  const roundedValue = Math.round(Number(value || 0) * 1000000) / 1000000;
  const wholeQuantity = Math.floor(roundedValue);

  return Math.min(Math.max(wholeQuantity, 0), Math.floor(Number(maxValue || 0)));
}

function getInventoryQuantity(inventoryItem) {
  const labelMatch = String(inventoryItem.quantityLabel || '').match(/[\d,.]+/);

  if (labelMatch) {
    return Number(labelMatch[0].replace(/,/g, ''));
  }

  const numericQuantity = Number(inventoryItem.quantity || inventoryItem.originalQuantity || 0);

  return numericQuantity > 0 ? numericQuantity : 0;
}

function getFclSlotNetKg(fclSlot) {
  const slotSacks = parsePositiveNumber(fclSlot?.draft?.sacks);
  const slotGrossKg = parsePositiveNumber(fclSlot?.draft?.grossKg);

  if (slotSacks <= 0 || slotGrossKg <= 0) {
    return 0;
  }

  return roundQuantity(Math.max(0, slotGrossKg - slotSacks / 2));
}

function parsePositiveNumber(value) {
  const numericValue = Number(String(value ?? '').replace(/,/g, ''));

  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

function roundQuantity(value) {
  return Number(Number(value || 0).toFixed(2));
}

function formatKg(value) {
  return `${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kg`;
}

function normalizeWholeSacksInput(value) {
  const numericValue = Number(String(value ?? '').replace(/[^\d.]/g, ''));

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return '';
  }

  return String(Math.floor(numericValue));
}

function normalizeQuantityInput(value) {
  const rawValue = String(value ?? '');
  const sanitizedValue = rawValue.replace(/[^\d.]/g, '');

  if (!sanitizedValue) {
    return '';
  }

  const [wholePartRaw = '', ...decimalParts] = sanitizedValue.split('.');
  const normalizedWholePart = wholePartRaw.replace(/^0+(?=\d)/, '');

  if (!decimalParts.length) {
    return normalizedWholePart;
  }

  const decimalPart = decimalParts.join('').slice(0, 2);
  return `${normalizedWholePart || '0'}.${decimalPart}`;
}

function loadStoredReleasingTab() {
  if (typeof window === 'undefined') {
    return 'pending';
  }

  const storedTab = window.localStorage.getItem(RELEASING_TAB_STORAGE_KEY);
  return RELEASING_TAB_IDS.has(storedTab) ? storedTab : 'pending';
}

function getTodayInputValue() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  return String(value).slice(0, 10);
}

export default useReleasings;
